import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import { 
  CalendarIcon,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

export default function GeneralLedger() {
  const { currentWorkspace } = useWorkspace();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!currentWorkspace) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(
          `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts?active_only=false`
        );
        
        if (response.ok) {
          const data = await response.json();
          setAccounts(data);
        }
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [currentWorkspace]);

  useEffect(() => {
    const fetchLedger = async () => {
      if (!currentWorkspace || !selectedAccount) {
        setLedgerData(null);
        return;
      }
      
      setLoading(true);
      try {
        let url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/ledger/account/${selectedAccount}`;
        const params = new URLSearchParams();
        
        if (startDate) {
          params.append('start_date', format(startDate, 'yyyy-MM-dd'));
        }
        if (endDate) {
          params.append('end_date', format(endDate, 'yyyy-MM-dd'));
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          setLedgerData(data);
        }
      } catch (error) {
        console.error('Failed to fetch ledger:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLedger();
  }, [currentWorkspace, selectedAccount, startDate, endDate]);

  if (loading && accounts.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full max-w-sm" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="general-ledger">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">General Ledger</h1>
        <p className="text-muted-foreground">
          View account activity and running balances
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 max-w-md">
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger data-testid="account-select">
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.account_id}>
                      <span className="font-mono">{account.code}</span> - {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {startDate ? format(startDate, 'MMM d, yyyy') : 'Start Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {endDate ? format(endDate, 'MMM d, yyyy') : 'End Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                />
              </PopoverContent>
            </Popover>

            {(startDate || endDate) && (
              <Button 
                variant="ghost" 
                onClick={() => { setStartDate(null); setEndDate(null); }}
              >
                Clear Dates
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ledger Data */}
      {!selectedAccount ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Select an account to view its ledger
            </p>
          </CardContent>
        </Card>
      ) : loading ? (
        <Skeleton className="h-96" />
      ) : ledgerData ? (
        <>
          {/* Account Info */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Account</p>
                  <p className="text-xl font-semibold">
                    <span className="font-mono">{ledgerData.account.code}</span> - {ledgerData.account.name}
                  </p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {ledgerData.account.type} • Normal: {ledgerData.account.normal_side}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Ending Balance</p>
                  <p className={cn(
                    "text-2xl font-bold font-mono",
                    ledgerData.ending_balance >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"
                  )} data-testid="ending-balance">
                    {formatCurrency(ledgerData.ending_balance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ledger Lines */}
          <Card>
            <CardHeader>
              <CardTitle>Account Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Date</TableHead>
                    <TableHead className="w-20">Entry #</TableHead>
                    <TableHead>Memo</TableHead>
                    <TableHead className="w-28 text-right">Debit</TableHead>
                    <TableHead className="w-28 text-right">Credit</TableHead>
                    <TableHead className="w-32 text-right">Balance</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerData.lines.length > 0 ? (
                    ledgerData.lines.map((line) => (
                      <TableRow key={line.ledger_id} data-testid={`ledger-line-${line.entry_no}`}>
                        <TableCell className="font-mono text-sm">
                          {line.txn_date}
                        </TableCell>
                        <TableCell className="font-mono">
                          {line.entry_no}
                        </TableCell>
                        <TableCell className="truncate max-w-[200px]">
                          {line.memo || <span className="text-muted-foreground italic">-</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {line.debit > 0 ? (
                            <span className="debit-amount">{formatCurrency(line.debit)}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {line.credit > 0 ? (
                            <span className="credit-amount">{formatCurrency(line.credit)}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-mono font-medium",
                          line.balance >= 0 ? "text-foreground" : "text-destructive"
                        )}>
                          {formatCurrency(line.balance)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/journal-entries/${line.entry_id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <p className="text-muted-foreground">No transactions for this account</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
